// Tiny sessionStorage layer + pub/sub that lets the client (owner code) filter
// and the property filter persist across all pages that expose them:
// Owner pages (Performance, Calendar, My Reservations, …), Reservations Report,
// and Guest Ratings / Guest Reviews.
//
// Empty array [] is a valid stored value (means "no selection").
// `null` returned by read = key absent / unparseable → caller falls back to its own default.
//
// Subscribers are notified synchronously after every write so multiple
// providers / pages mounted at the same time stay in lockstep — this is what
// makes persistence work even when one provider lives at the App root and
// another mounts later on a different route.

import { useSyncExternalStore } from "react";

const SHARED_CLIENT_KEY = "sharedClientCodes:v1";
const SHARED_PROPS_KEY = "sharedPropertyIds:v1";
const ANON_SCOPE = "__anonymous__";

let activeScope = ANON_SCOPE;

function scopedKey(baseKey: string): string {
  return `${baseKey}:${activeScope}`;
}

// ─── In-memory cache + subscriber registry ───────────────────────────────────

type Listener = () => void;

interface Store {
  value: string[] | null;
  listeners: Set<Listener>;
  loadedKey: string | null;
}

const clientStore: Store = { value: null, listeners: new Set(), loadedKey: null };
const propsStore: Store = { value: null, listeners: new Set(), loadedKey: null };

function loadFromStorage(key: string): string[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw == null) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((v): v is string => typeof v === "string");
  } catch {
    return null;
  }
}

function ensureLoaded(store: Store, key: string) {
  if (store.loadedKey === key) return;
  store.value = loadFromStorage(key);
  store.loadedKey = key;
}

function arraysEqual(a: string[] | null, b: string[] | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function setStoreSnapshot(store: Store, key: string, next: string[] | null) {
  if (store.loadedKey === key && arraysEqual(store.value, next)) return;
  if (arraysEqual(store.value, next)) {
    store.loadedKey = key;
    return;
  }
  store.value = next ? next.slice() : null;
  store.loadedKey = key;
}

function writeStore(store: Store, key: string, next: string[]) {
  ensureLoaded(store, key);
  if (arraysEqual(store.value, next)) return;
  store.value = next.slice();
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* ignore quota / private mode */
    }
  }
  store.listeners.forEach((l) => l());
}

function subscribeStore(store: Store, key: string, listener: Listener): () => void {
  ensureLoaded(store, key);
  store.listeners.add(listener);
  return () => {
    store.listeners.delete(listener);
  };
}

function notifyStore(store: Store) {
  store.listeners.forEach((l) => l());
}

function syncStoreToActiveScope(store: Store, key: string) {
  setStoreSnapshot(store, key, loadFromStorage(key));
}

function isScopedStorageEventKey(storageKey: string | null, baseKey: string): boolean {
  return storageKey === scopedKey(baseKey);
}

// Multi-tab safety: react to writes from other tabs.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (isScopedStorageEventKey(e.key, SHARED_CLIENT_KEY)) {
      syncStoreToActiveScope(clientStore, scopedKey(SHARED_CLIENT_KEY));
      notifyStore(clientStore);
    } else if (isScopedStorageEventKey(e.key, SHARED_PROPS_KEY)) {
      syncStoreToActiveScope(propsStore, scopedKey(SHARED_PROPS_KEY));
      notifyStore(propsStore);
    }
  });
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function readSharedClientCodes(): string[] | null {
  ensureLoaded(clientStore, scopedKey(SHARED_CLIENT_KEY));
  return clientStore.value;
}

export function writeSharedClientCodes(codes: string[]): void {
  writeStore(clientStore, scopedKey(SHARED_CLIENT_KEY), codes);
}

export function subscribeSharedClientCodes(listener: Listener): () => void {
  return subscribeStore(clientStore, scopedKey(SHARED_CLIENT_KEY), listener);
}

export function readSharedPropertyIds(): string[] | null {
  ensureLoaded(propsStore, scopedKey(SHARED_PROPS_KEY));
  return propsStore.value;
}

export function writeSharedPropertyIds(ids: string[]): void {
  writeStore(propsStore, scopedKey(SHARED_PROPS_KEY), ids);
}

export function subscribeSharedPropertyIds(listener: Listener): () => void {
  return subscribeStore(propsStore, scopedKey(SHARED_PROPS_KEY), listener);
}

export function setSharedFilterScope(userId: string | null | undefined): void {
  const nextScope = userId && userId.trim().length > 0 ? userId : ANON_SCOPE;
  if (activeScope === nextScope) return;
  activeScope = nextScope;
  const clientKey = scopedKey(SHARED_CLIENT_KEY);
  const propsKey = scopedKey(SHARED_PROPS_KEY);
  const prevClient = clientStore.value;
  const prevProps = propsStore.value;
  syncStoreToActiveScope(clientStore, clientKey);
  syncStoreToActiveScope(propsStore, propsKey);
  if (!arraysEqual(prevClient, clientStore.value)) notifyStore(clientStore);
  if (!arraysEqual(prevProps, propsStore.value)) notifyStore(propsStore);
}

export function resetSharedFilterScope(): void {
  setSharedFilterScope(null);
}

/**
 * Hard-wipe every scoped shared-filter entry in sessionStorage and reset the
 * in-memory stores to `null`. Called on sign-out so the next login starts
 * from a clean slate and the seed effect in OwnerFilterContext re-seeds
 * listed-only defaults for client/client_test (and unset for admin/AM).
 */
export function clearAllSharedFilterStorage(): void {
  if (typeof window !== "undefined") {
    try {
      Object.keys(window.sessionStorage).forEach((k) => {
        if (k.startsWith(`${SHARED_CLIENT_KEY}:`) || k.startsWith(`${SHARED_PROPS_KEY}:`)) {
          try { window.sessionStorage.removeItem(k); } catch { /* ignore */ }
        }
      });
    } catch { /* ignore */ }
  }
  clientStore.value = null;
  clientStore.loadedKey = null;
  propsStore.value = null;
  propsStore.loadedKey = null;
  notifyStore(clientStore);
  notifyStore(propsStore);
}


// ─── React hooks (re-render on every cross-page change) ──────────────────────

export function useSharedClientCodes(): string[] | null {
  return useSyncExternalStore(
    subscribeSharedClientCodes,
    readSharedClientCodes,
    () => null,
  );
}

export function useSharedPropertyIds(): string[] | null {
  return useSyncExternalStore(
    subscribeSharedPropertyIds,
    readSharedPropertyIds,
    () => null,
  );
}
